<template>
  <el-dialog
    title="選擇堂票"
    :visible="true"
    width="700px"
    :close-on-click-modal="false"
    append-to-body
    @close="$emit('close')"
  >
    <ElInputWrapper>
      <header
        class="grid"
        style="gap: 16px"
        :class="{
          'grid-cols-2': !isBenefitEnabled,
          'grid-cols-3': isBenefitEnabled,
        }"
      >
        <BaseElInput v-model="search.name" placeholder="請輸入名稱" clearable>
          <i slot="suffix" class="el-input__icon el-icon-search" />
        </BaseElInput>
        <BaseElSelect
          v-if="showControl.search.sharingType"
          v-model="search.sharingType"
          placeholder="請選擇通用類型"
          clearable
          @change="emitFilter"
        >
          <BaseElSelectOption label="分店專屬" value="shop" />
          <BaseElSelectOption label="組織通用" value="org" />
        </BaseElSelect>

        <BaseElSelect
          v-if="showControl.search.issuingShop"
          v-model="search.issuingShopId"
          placeholder="請選擇發行店"
          clearable
          @change="emitFilter"
        >
          <BaseElSelectOption
            v-for="shop in classTicketSourceShops"
            :key="shop.id"
            :label="shop.name"
            :value="shop.id"
          />
        </BaseElSelect>
      </header>
    </ElInputWrapper>

    <main>
      <div
        class="grid-container text-primary-100"
        :class="{ 'grid-org': isBenefitEnabled }"
        style="padding-top: 21px; padding-bottom: 16px"
      >
        <BaseElCheckbox label="產品名稱" :disabled="!multiple" @change="selectAll">
          <p class="text-primary-100">名稱</p>
        </BaseElCheckbox>
        <p v-if="isBenefitEnabled" class="text-center">發行店</p>
        <p class="text-center">有效期限</p>
        <p v-if="isBenefitEnabled" class="text-center">到期提醒天數</p>
      </div>

      <BaseElCheckboxGroup v-model="selected">
        <div
          v-for="product in displayData[tableOptions.page - 1]"
          :key="product.id"
        >
          <hr>
          <div
            class="grid-container"
            :class="{ 'grid-org': isBenefitEnabled }"
            style="padding: 16px 0"
          >
            <BaseElCheckbox
              :label="product.id"
              :disabled="isProductDisabled(product)"
            >
              <div class="flex items-center relative min-w-0" style="gap: 4px">
                <el-tooltip effect="dark" :content="getSourceLabel(product)" placement="top">
                  <SVGIcon
                    :icon="getSourceIcon(product)"
                    stroke="var(--gray-80)"
                    fill="transparent"
                    width="20"
                    height="20"
                    class="flex-shrink-0"
                  />
                </el-tooltip>
                <el-tooltip effect="dark" :content="product.name" placement="top">
                  <span class="truncate">{{ product.name }}</span>
                </el-tooltip>
                <TipInfo v-if="disabledReason(product)" :size="20">
                  {{ disabledReason(product) }}
                </TipInfo>
              </div>
            </BaseElCheckbox>
            <p v-if="isBenefitEnabled" class="text-center truncate">{{ product.Shop?.name || '-' }}</p>
            <p class="text-center">
              {{ formatExpPreviewText({
                isExp: product.isExp,
                expType: product.expType,
                expDay: product.expDay,
                endOfPeriodUnit: product.endOfPeriodUnit,
                specifyExpiredDate: product.specifyExpiredDate,
              }) }}
            </p>
            <el-tooltip v-if="isBenefitEnabled" effect="dark" :content="formatExpNotifyDays(product)" placement="top">
              <p class="text-center">{{ formatExpNotifyDays(product) }}</p>
            </el-tooltip>
          </div>
        </div>
      </BaseElCheckboxGroup>
    </main>

    <div>
      <Pagination
        :curPage.sync="tableOptions.page"
        :pageLimit="tableOptions.pageLimit"
        :total="flattenDeep(displayData).length"
      />
    </div>

    <div class="flex justify-end items-center" style="padding-top: 26px">
      <BaseElButton plain @click="$emit('close')">取消</BaseElButton>
      <BaseElButton type="primary" :disabled="!selected.length" @click="onSubmit">
        確認
      </BaseElButton>
    </div>
  </el-dialog>
</template>

<script>
import { defineComponent, onMounted, reactive, ref, computed, watch } from 'vue'
import { map, chunk, get, flattenDeep } from 'lodash'
import { useTable } from '@/use/table'
import { formatDate } from '@/utils/date'
import dayjs from '@/lib/dayjs'
import { isClassTicketConfigExpired, useClassTicket } from '@/use/classTicket'
import { isBenefitSharingEnabled } from '@/use/useOrgBenefitControl'
import TipInfo from '../TipInfo.vue'
import SVGIcon from '@/components/SVGIcon.vue'
import { useShop } from '@/use/shop'
import store from '@/store'
import ElInputWrapper from '@/components/ElInputWrapper.vue'

export default defineComponent({
  name: 'ClassTicketAdvanceSelect',
  components: { TipInfo, SVGIcon, ElInputWrapper },
  props: {
    data: { type: Array, default: () => [] },
    selectedData: { type: Array, default: () => [] },
    multiple: { type: Boolean, default: false },
    max: { type: [Number, null], default: null },
    disabledExp: Boolean,
  },
  emits: ['close', 'confirm', 'filter'],
  setup (props, { emit }) {
    const { formatExpPreviewText } = useClassTicket()
    const { tableData, tableDataCount, tableOptions } = useTable()
    const { shopId } = useShop()
    const isBenefitEnabled = computed(() => isBenefitSharingEnabled('classTicket', shopId.value))

    const classTicketReminderRules = computed(() => {
      return get(store.state, 'shop.shopSetting.benefitSharing.classTicket.reminderRules', [])
    })

    const classTicketSourceShops = computed(() => {
      return get(store.state, 'shop.shopSetting.benefitSharing.classTicket.sourceShops', [])
    })

    const enrichedData = computed(() => {
      if (!isBenefitEnabled.value) return props.data
      return props.data.map((item) => {
        if (item.expireNotificationDays != null) return item
        const shopKey = item.Shop?.id || item.sourceShopId
        if (!shopKey) return item
        const rule = classTicketReminderRules.value.find(r => r.shopId === shopKey)
        const expireNotificationDays = rule?.expireNotificationDays
        if (expireNotificationDays == null) return item
        return { ...item, expireNotificationDays }
      })
    })

    const formatExpNotifyDays = (product) => {
      const days = product.expireNotificationDays
      if (days == null || !days.length) return '-'
      return days.join(', ') + ' 天'
    }

    const showControl = computed(() => {
      const controls = {
        // 通用類型
        search: {
          sharingType: isBenefitEnabled.value,
          issuingShop: isBenefitEnabled.value,
        },
      }
      return controls
    })

    const getBenefitShopScope = (sharingType) => {
      if (sharingType === 'shop') return 'local'
      if (sharingType === 'org') return 'shared'
      return undefined
    }

    const getSourceIcon = (item) => {
      if (item.Shop?.id && item.Shop.id !== shopId.value) return 'common-org_icon'
      return 'common-shop_icon'
    }
    const getSourceLabel = (item) => {
      if (item.Shop?.id && item.Shop.id !== shopId.value) return '組織通用'
      return '分店專屬'
    }
    tableOptions.pageLimit = 8
    const displayData = computed(() => {
      let data = [...enrichedData.value]
      if (search.name) data = data.filter((i) => i.name.includes(search.name))
      if (search.sharingType) {
        if (search.sharingType === 'shop') data = data.filter(i => i.Shop?.id === shopId.value)
        else if (search.sharingType === 'org') data = data.filter(i => !i.Shop || i.Shop.id !== shopId.value)
      }
      return chunk(data, tableOptions.pageLimit)
    })
    const isProductDisabled = (product) => {
      const productId = product.id

      // 1. 如果是已選中的項目，永遠允許取消選中
      if (selected.value.includes(productId)) {
        return false
      }

      // 2. 檢查過期狀態（只對未選中項目生效）
      if (props.disabledExp && checkExp(product)) {
        return true
      }

      // 3. 檢查數量限制
      if (props.max && selected.value.length >= props.max) {
        return true
      }

      // 4. 單選模式下，如果已有選中項目，禁用其他項目
      if (!props.multiple && selected.value.length > 0) {
        return true
      }

      return false
    }
    const checkExp = (product) => {
      if (!props.disabledExp) return false
      return isClassTicketConfigExpired(product)
    }
    const search = reactive({
      name: null,
      sharingType: null,
      issuingShopId: null,
    })
    const selected = ref([])

    // 計算目標頁面的函式
    const findTargetPage = (classTicketId) => {
      if (!classTicketId) return 1

      // 在完整資料列表中找到目標項目的索引
      let filteredData = [...enrichedData.value]

      // 套用篩選條件
      if (search.name) filteredData = filteredData.filter(i => i.name.includes(search.name))
      if (search.sharingType) {
        if (search.sharingType === 'shop') filteredData = filteredData.filter(i => i.Shop?.id === shopId.value)
        else if (search.sharingType === 'org') filteredData = filteredData.filter(i => !i.Shop || i.Shop.id !== shopId.value)
      }

      const targetIndex = filteredData.findIndex(item => item.id === classTicketId)
      if (targetIndex === -1) return 1

      // 計算目標頁面（每頁 8 筆）
      return Math.ceil((targetIndex + 1) / tableOptions.pageLimit)
    }

    // 檢查選中的項目是否在當前篩選結果中
    const isSelectedItemVisible = (classTicketId) => {
      if (!classTicketId) return true

      let filteredData = [...enrichedData.value]
      if (search.name) filteredData = filteredData.filter(i => i.name.includes(search.name))

      return filteredData.some(item => item.id === classTicketId)
    }

    // 初始化選中狀態和頁面跳轉的函式
    const initializeSelectionAndPage = () => {
      if (props.selectedData && props.selectedData.length > 0) {
        selected.value = props.selectedData

        // 如果有選中項目，跳轉到第一個選中項目所在的頁面
        const firstSelectedId = props.selectedData[0]
        const itemExists = enrichedData.value.some(item => item.id === firstSelectedId)

        if (itemExists) {
          const targetPage = findTargetPage(firstSelectedId)
          tableOptions.page = targetPage
        }
      }
    }

    const selectAll = (all) => {
      if (all) {
        selected.value = map(displayData.value[tableOptions.page - 1], 'id')
        if (props.max) selected.value = selected.value.slice(0, props.max)
      } else selected.value = []
    }

    const disabledReason = (product) => {
      if (checkExp(product)) return '已過期'
      return null
    }

    const onSubmit = () => {
      emit('confirm', selected.value)
      emit('close')
    }

    const emitFilter = () => {
      const filters = {
        benefitShopScope: getBenefitShopScope(search.sharingType),
      }

      if (showControl.value.search.issuingShop) {
        filters.issuingShopId = search.issuingShopId || undefined
      }

      tableOptions.page = 1
      emit('filter', filters)
    }

    onMounted(() => {
      tableData.value = enrichedData.value
      tableDataCount.value = enrichedData.value.length

      // 初始化選中狀態和頁面跳轉
      initializeSelectionAndPage()
    })

    // 監聽篩選條件變化，重新定位頁面
    watch([() => search.name, () => search.sharingType], () => {
      if (selected.value.length > 0) {
        const firstSelectedId = selected.value[0]

        // 檢查選中項目是否在篩選結果中
        if (isSelectedItemVisible(firstSelectedId)) {
          const targetPage = findTargetPage(firstSelectedId)
          tableOptions.page = targetPage
        } else {
          // 如果選中項目被篩選掉了，回到第一頁
          tableOptions.page = 1
        }
      }
    })

    return {
      get,
      flattenDeep,
      onSubmit,
      search,
      selected,
      selectAll,
      displayData,
      tableDataCount,
      tableOptions,
      isProductDisabled,
      disabledReason,
      dayjs,
      formatDate,
      checkExp,
      formatExpPreviewText,
      findTargetPage,
      isSelectedItemVisible,
      initializeSelectionAndPage,
      getSourceIcon,
      getSourceLabel,
      isBenefitEnabled,
      showControl,
      emitFilter,
      formatExpNotifyDays,
      classTicketSourceShops,
    }
  },
})
</script>

<style scoped lang="postcss">
.grid-container {
  @apply grid gap-[20px];
  @apply text-normal;
  grid-template-columns: 1fr 260px;
}
.grid-org {
  grid-template-columns: 1fr 140px 140px 100px;
}
::v-deep .el-input {
  @apply w-full;
}
::v-deep .el-select {
  @apply w-full;
}
::v-deep .el-checkbox {
  @apply flex items-center w-[180px];
}
::v-deep .el-checkbox .el-checkbox__label {
  @apply truncate w-full flex items-center;
}
</style>
